import { useMemo } from 'react';

import { gql, useApolloClient } from '@apollo/client';

import {
  MaterializedSegmentFragmentDoc,
  isType,
  useMaterializedSegmentsQuery,
  useSegmentsQuery,
} from '@spotify/confidence-graphql';

import { Criterion } from './types';

const SEGMENT_WITH_LABELS_FRAGMENT = gql`
  fragment SegmentWithLabels on FlagsAdminV1Segment {
    __typename
    name
    displayName
    labels {
      key
      value
    }
  }
`;

const buildNameFilter = (names: string[]) =>
  `name:(${names.map(n => `"${n}"`).join(' OR ')})`;

export const useResolveCriterionDisplayNames = (
  criteria: Record<string, Criterion>,
): Record<string, Criterion> => {
  const client = useApolloClient();

  const allSegmentNames = useMemo(
    () =>
      Object.values(criteria)
        .map(c => c.segment?.segment)
        .filter((name): name is string => !!name),
    [criteria],
  );

  const missingSegmentNames = useMemo(
    () =>
      allSegmentNames.filter(
        name =>
          !client.readFragment({
            id: client.cache.identify({
              __typename: 'FlagsAdminV1Segment',
              name,
            }),
            fragment: SEGMENT_WITH_LABELS_FRAGMENT,
          }),
      ),
    [client, allSegmentNames],
  );

  const { data: segmentsData } = useSegmentsQuery({
    skip: missingSegmentNames.length === 0,
    variables: {
      filter: buildNameFilter(missingSegmentNames),
      pageSize: 50,
    },
  });

  const fetchedSegmentsByName = useMemo(() => {
    if (!isType(segmentsData?.segments, 'FlagsAdminV1ListSegmentsResponse')) {
      return {};
    }
    return Object.fromEntries(
      segmentsData.segments.segments.map(s => [s.name, s]),
    );
  }, [segmentsData]);

  const allMaterializedSegmentNames = useMemo(
    () =>
      Object.values(criteria)
        .map(c => c.materializedSegment?.materializedSegment)
        .filter((name): name is string => !!name),
    [criteria],
  );

  const missingMaterializedSegmentNames = useMemo(
    () =>
      allMaterializedSegmentNames.filter(
        name =>
          !client.readFragment({
            id: client.cache.identify({
              __typename: 'FlagsAdminV1MaterializedSegment',
              name,
            }),
            fragment: MaterializedSegmentFragmentDoc,
            fragmentName: 'MaterializedSegment',
          }),
      ),
    [client, allMaterializedSegmentNames],
  );

  const { data: materializedSegmentsData } = useMaterializedSegmentsQuery({
    skip: missingMaterializedSegmentNames.length === 0,
    variables: {
      filter: buildNameFilter(missingMaterializedSegmentNames),
      pageSize: 50,
    },
  });

  const fetchedMaterializedByName = useMemo(() => {
    if (
      !isType(
        materializedSegmentsData?.materializedSegments,
        'FlagsAdminV1ListMaterializedSegmentsResponse',
      )
    ) {
      return {};
    }
    return Object.fromEntries(
      materializedSegmentsData.materializedSegments.materializedSegments.map(
        s => [s.name, s],
      ),
    );
  }, [materializedSegmentsData]);

  return useMemo(() => {
    const resolved: Record<string, Criterion> = {};

    for (const [key, criterion] of Object.entries(criteria)) {
      if (criterion.segment?.segment) {
        const segmentName = criterion.segment.segment;
        const cached = client.readFragment({
          id: client.cache.identify({
            __typename: 'FlagsAdminV1Segment',
            name: segmentName,
          }),
          fragment: SEGMENT_WITH_LABELS_FRAGMENT,
        });
        const fetched = fetchedSegmentsByName[segmentName];
        const source = cached ?? fetched;
        if (source) {
          resolved[key] = {
            ...criterion,
            segment: {
              ...criterion.segment,
              displayName:
                criterion.segment.displayName ??
                source.displayName ??
                undefined,
              labels: criterion.segment.labels ?? source.labels ?? undefined,
            },
          };
          continue;
        }
      }

      if (
        criterion.materializedSegment?.materializedSegment &&
        !criterion.materializedSegment.displayName
      ) {
        const segmentName = criterion.materializedSegment.materializedSegment;
        const cached = client.readFragment({
          id: client.cache.identify({
            __typename: 'FlagsAdminV1MaterializedSegment',
            name: segmentName,
          }),
          fragment: MaterializedSegmentFragmentDoc,
          fragmentName: 'MaterializedSegment',
        });
        const fetched = fetchedMaterializedByName[segmentName];
        const source = cached ?? fetched;
        if (source?.displayName) {
          resolved[key] = {
            ...criterion,
            materializedSegment: {
              ...criterion.materializedSegment,
              displayName: source.displayName,
            },
          };
          continue;
        }
      }

      resolved[key] = criterion;
    }

    return resolved;
  }, [client, criteria, fetchedSegmentsByName, fetchedMaterializedByName]);
};
